/**
 * Invoices waiting on something — the queues support works through ("which
 * payouts are on hold", "failed payments this week", "any refund requests for
 * Acme") — and a buyer's own "do I have any failed payments".
 *
 * A queue is the admin Invoices page's Transaction Status filter, so the count
 * here is the count an admin sees after picking that status. Invoices leave a
 * queue when they move on — a failed payment stops being one once it is paid —
 * so with no period named a queue is everything in it right now, however old.
 * A period narrows it to invoices *created* in that window, which is what the
 * page's dates filter on.
 *
 * Payout holds are the exception: "Put payout on hold" sets a flag and leaves
 * the status alone, so that queue filters on the flag instead, and has no page
 * filter to link to. The supplier's invoice search has no status filter at all,
 * so suppliers are not served here; their payouts have skills of their own.
 */

import { AccountType } from '../constants/securspace-constants';
import { containsFuzzyPhrase, tokenize } from './fuzzyMatch';
import { findCompaniesInMessage, mayNameCompany } from './companyNameMatch';
import { invoicePageLink, isRefund, REFUND_TRANSACTION_TYPES, statusLabelFor } from './invoiceStatus';
import type {
  KnowledgeAnswer,
  KnowledgeBreakdownRow,
  KnowledgeContext,
  KnowledgeLink,
  KnowledgeQuery,
} from './knowledgeRegistry';
import { displayDate, money, shiftedDay } from './lookupFormat';
import { asksForTimeComparison } from './comparisonQuestion';
import { classifyLookup, isHowToOrActionRequest } from './lookupRouting';
import { fetchCompanyNames, type CompanyName } from './requests/account-lookup-requests';
import { apiDateRange } from './requests/apiDates';
import {
  countInvoiceQueue,
  findInvoiceQueue,
  type InvoiceLookupRow,
  type QueueSelector,
  type StatusQueue,
  type StatusQueueFilter,
} from './requests/invoice-lookup-requests';

/** Whose money a queue is about: the buyer's charge or the supplier's payout. */
type Side = 'buyer' | 'supplier';

interface InvoiceQueue {
  selector: QueueSelector;
  /** Phrases naming the queue, matched typo-tolerantly. The longest match wins. */
  phrases: string[];
  /** Completes "1 invoice …" and "3 invoices …". */
  has: [one: string, many: string];
  side: Side;
  /** What the queue means or what clears it, when that is not obvious. */
  meaning?: string;
}

/** Invoices listed under an answer. The rest are a click away. */
const MAX_LISTED = 10;

/** Longest processor message quoted on a row; they can run to a paragraph. */
const MAX_MESSAGE_LENGTH = 60;

const ADMIN_QUEUES: InvoiceQueue[] = [
  {
    selector: { status: 'PAYMENT_FAILED' },
    phrases: [
      'failed payments',
      'failed payment',
      'payment failed',
      'payments failed',
      'payment failures',
      'failed charges',
      'failed charge',
      'declined payments',
      'failed invoices',
      'invoices failed',
      'invoices that failed',
      'unpaid invoices',
    ],
    has: ['has a failed payment', 'have a failed payment'],
    side: 'buyer',
    meaning:
      'A failed buyer payment stays here until the buyer pays it from **Bookings \u2192 Payment ' +
      'Failed \u2192 Make Payment**. The status also covers failed refunds and failed transfers from ' +
      'the payment processor to SecurSpace; each row shows which.',
  },
  {
    selector: { payoutOnHold: true },
    phrases: [
      'payouts on hold',
      'payout on hold',
      'held payouts',
      'payouts held',
      'payout holds',
      'payouts being held',
      'on hold payouts',
    ],
    has: ['has its supplier payout on hold', 'have their supplier payout on hold'],
    side: 'supplier',
    meaning:
      'A payout is put on hold by an admin or by a dispute, and is released with **Take payout ' +
      'off hold** on the invoice.',
  },
  {
    selector: { status: 'PAYOUT_FAILED' },
    phrases: ['failed payouts', 'failed payout', 'payouts failed', 'payout failed', 'payout failures'],
    has: ['has a failed supplier payout', 'have a failed supplier payout'],
    side: 'supplier',
    meaning: 'Check the supplier\u2019s payout bank account.',
  },
  {
    selector: { status: 'CHARGE_ON_HOLD' },
    phrases: ['charges on hold', 'charge on hold', 'held charges', 'charges held'],
    has: ['has its charge on hold', 'have their charge on hold'],
    side: 'buyer',
    meaning: 'The buyer has not been charged for these, so there is nothing to pay out yet.',
  },
  {
    selector: { status: 'REFUND_REQUESTED' },
    phrases: [
      'refund requests',
      'refund request',
      'requested refunds',
      'refunds requested',
      'refund requested',
      'pending refunds',
      'refunds pending',
      'open refunds',
    ],
    has: ['has a refund requested', 'have a refund requested'],
    side: 'buyer',
    meaning: 'Each is waiting for the refund to be approved or denied.',
  },
  {
    selector: { status: 'REFUND_DENIED' },
    phrases: ['denied refunds', 'refunds denied', 'refund denied', 'rejected refunds', 'refunds rejected'],
    has: ['has a denied refund', 'have a denied refund'],
    side: 'buyer',
  },
  {
    selector: { status: 'UNCOLLECTIBLE' },
    phrases: ['uncollectible', 'written off', 'write offs', 'bad debt'],
    has: ['is marked uncollectible', 'are marked uncollectible'],
    side: 'buyer',
    meaning: 'They will not be paid out.',
  },
  {
    selector: { status: 'DECLINED' },
    phrases: ['declined charges', 'declined invoices', 'disputed invoices', 'disputed charges', 'disputed payments'],
    has: ['has a declined charge', 'have a declined charge'],
    side: 'buyer',
    meaning: 'A declined charge has to be resolved manually.',
  },
  {
    selector: { status: 'PAYOUT_PENDING' },
    phrases: ['pending payouts', 'payouts pending', 'payouts in progress', 'payouts in transit', 'payouts on the way'],
    has: ['has a supplier payout on its way', 'have a supplier payout on its way'],
    side: 'supplier',
  },
  {
    selector: { status: 'PAYOUT_READY' },
    phrases: [
      'ready for payout',
      'ready to pay out',
      'ready to be paid out',
      'awaiting payout',
      'waiting for payout',
      'waiting on payout',
    ],
    has: ['is ready for payout', 'are ready for payout'],
    side: 'supplier',
    meaning: 'The buyer has paid, and each goes out with the supplier\u2019s next payout.',
  },
  {
    selector: { status: 'PAYMENT_PENDING' },
    phrases: ['pending payments', 'payments pending', 'payments processing', 'processing payments', 'payments in progress'],
    has: ['has a payment still processing', 'have a payment still processing'],
    side: 'buyer',
  },
  {
    selector: { status: 'MANUAL_PAYMENT' },
    phrases: ['manual payments', 'manual payment', 'paid manually', 'manually paid'],
    has: ['is paid manually', 'are paid manually'],
    side: 'buyer',
    meaning: 'They are paid outside SecurSpace, so SecurSpace neither charges nor pays them out.',
  },
];

/** A buyer asking whether anything of theirs is unpaid. */
const BUYER_OWES_PHRASES = [
  'failed payments',
  'failed payment',
  'payment failed',
  'payments failed',
  'payment failures',
  'failed charges',
  'declined payments',
  'unpaid invoices',
  'unpaid bills',
  'overdue invoices',
  'overdue payments',
  'past due',
  'outstanding invoices',
  'outstanding balance',
  'balance due',
  'amount due',
  'do i owe',
  'what i owe',
  'owe anything',
  'owe money',
];

/**
 * Questions that name a queue's words but want something else: why one payment
 * failed, or what is owed on one booking — the diagnostics and lookups answer
 * those. Overages have a skill of their own for "how much do I owe for overages".
 */
const NOT_A_QUEUE =
  /\bwhy\b|\b(?:on|for)\s+(?:(?:this|that|the|a|my|our)\s+)?(?:booking|order|invoice)\b|\boverages?\b/i;

/**
 * Words dropped before matching, so a phrase survives being said as a sentence:
 * "payouts on hold" also matches "payouts that are currently on hold", and
 * "payouts failed" matches "payouts that have failed".
 */
const FILLER = new Set(['are', 'is', 'was', 'were', 'been', 'being', 'that', 'currently', 'still', 'have', 'has', 'had']);

const matchTokens = (text: string): string[] => tokenize(text).filter((token) => !FILLER.has(token));

const phraseTokens = (phrases: string[]): string[][] => phrases.map(matchTokens);

const QUEUE_TOKENS = ADMIN_QUEUES.map((queue) => phraseTokens(queue.phrases));
const BUYER_OWES_TOKENS = phraseTokens(BUYER_OWES_PHRASES);

/** The admin queue a message names, by its longest matching phrase, or null. */
export const pickAdminQueue = (message: string): InvoiceQueue | null => {
  const tokens = matchTokens(message);
  let best: InvoiceQueue | null = null;
  let bestLength = 0;
  for (let index = 0; index < ADMIN_QUEUES.length; index += 1) {
    const queue = ADMIN_QUEUES[index];
    for (let phraseIndex = 0; phraseIndex < queue.phrases.length; phraseIndex += 1) {
      const phrase = queue.phrases[phraseIndex];
      if (phrase.length <= bestLength) continue;
      if (!containsFuzzyPhrase(tokens, QUEUE_TOKENS[index][phraseIndex])) continue;
      best = queue;
      bestLength = phrase.length;
    }
  }
  return best;
};

const namesBuyerOwing = (message: string): boolean => {
  const tokens = matchTokens(message);
  return BUYER_OWES_TOKENS.some((phrase) => containsFuzzyPhrase(tokens, phrase));
};

const asksForAQueue = (message: string): boolean =>
  classifyLookup(message) === null && !isHowToOrActionRequest(message) && !NOT_A_QUEUE.test(message);

/**
 * Claims an admin question naming a queue ahead of the phrase match, where
 * "how many invoices have a failed payment" would otherwise go to the plain
 * invoice count for its longer "how many invoices".
 */
export const claimsAdminInvoiceQueue = (message: string): boolean =>
  asksForAQueue(message) && pickAdminQueue(message) !== null;

/** The same for a buyer's "do I have any failed payments" and "what do I owe". */
export const claimsBuyerFailedPayments = (message: string): boolean =>
  asksForAQueue(message) && namesBuyerOwing(message);

const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

const amountOf = (row: InvoiceLookupRow, side: Side): number => {
  const amount = side === 'supplier' ? row.supplierAmount : row.buyerAmount;
  return typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
};

const totalOf = (rows: InvoiceLookupRow[], side: Side): number =>
  rows.reduce((sum, row) => sum + amountOf(row, side), 0);

const invoiceLink = (audience: 'admin' | 'buyer', row: InvoiceLookupRow): KnowledgeLink | undefined => {
  const number = String(row.transactionNumber ?? '').trim();
  return number ? invoicePageLink(audience, { invoiceNumber: number }, { from: row.createdOn }) : undefined;
};

const processorMessage = (row: InvoiceLookupRow): string => {
  const text = (row.paymentMessage ?? '').trim();
  if (!text) return '';
  const short = text.length > MAX_MESSAGE_LENGTH ? `${text.slice(0, MAX_MESSAGE_LENGTH - 1).trimEnd()}\u2026` : text;
  return `\u201c${short}\u201d`;
};

const invoiceLabel = (row: InvoiceLookupRow): string => {
  const booking = (row.bookingNumber ?? '').trim();
  return `Invoice #${row.transactionNumber ?? '?'}${booking ? ` \u00b7 booking #${booking}` : ''}`;
};

/**
 * The raw status behind a row, when it says more than the queue's name: one
 * status group spans several raw states — "Payment Failed" covers a failed
 * transfer to SecurSpace as well as a buyer's failed charge — and a held payout
 * can be in any of them.
 */
const rawStatus = (row: InvoiceLookupRow, queue: InvoiceQueue): string => {
  if (!row.status) return '';
  if ('status' in queue.selector && row.status === queue.selector.status) return '';
  return statusLabelFor(row, 'admin');
};

const adminRow = (row: InvoiceLookupRow, queue: InvoiceQueue): KnowledgeBreakdownRow => {
  const buyer = row.buyerCompanyName?.trim();
  const supplier = row.supplierCompanyName?.trim();
  const parties = buyer && supplier ? `${buyer} \u2192 ${supplier}` : buyer || supplier || '';
  return {
    label: invoiceLabel(row),
    value: [
      parties,
      money(amountOf(row, queue.side)),
      displayDate(row.createdOn),
      rawStatus(row, queue),
      isRefund(row) ? 'refund' : '',
      processorMessage(row),
    ]
      .filter(Boolean)
      .join(' \u00b7 '),
    link: invoiceLink('admin', row),
  };
};

/** The company a question is scoped to, if it named one the platform has. */
type CompanyScope =
  | { kind: 'none' }
  | { kind: 'one'; company: CompanyName; side: Side }
  | { kind: 'ambiguous'; name: string }
  | { kind: 'unavailable' };

/**
 * Finds the company a question names, looking first among the side the queue is
 * about — a failed payment is a buyer's, a held payout a supplier's — and then
 * the other, since "failed payments for Yardco" can mean payments owed to it.
 */
const resolveCompany = async (message: string, side: Side): Promise<CompanyScope> => {
  if (!mayNameCompany(message)) return { kind: 'none' };

  let buyers: CompanyName[];
  let suppliers: CompanyName[];
  try {
    [buyers, suppliers] = await Promise.all([
      fetchCompanyNames(AccountType.BUYER as 'Buyer'),
      fetchCompanyNames(AccountType.SUPPLIER as 'Supplier'),
    ]);
  } catch (err) {
    console.warn('Assistant could not load company names:', err);
    return { kind: 'unavailable' };
  }

  const order: Array<[Side, CompanyName[]]> =
    side === 'buyer' ? [['buyer', buyers], ['supplier', suppliers]] : [['supplier', suppliers], ['buyer', buyers]];

  for (const [companySide, companies] of order) {
    const found = findCompaniesInMessage(message, companies);
    if (found.length === 1) return { kind: 'one', company: found[0], side: companySide };
    if (found.length > 1) return { kind: 'ambiguous', name: found[0].companyName };
  }
  return { kind: 'none' };
};

const accountFilter = (scope: CompanyScope): StatusQueueFilter => {
  if (scope.kind !== 'one') return {};
  return scope.side === 'buyer' ? { buyerAccountId: scope.company.id } : { supplierAccountId: scope.company.id };
};

const scopePhrase = (scope: CompanyScope): string => {
  if (scope.kind !== 'one') return ' across the platform';
  return ` for **${scope.company.companyName}** (as ${scope.side === 'buyer' ? 'the buyer' : 'the supplier'})`;
};

const scopeNote = (scope: CompanyScope): string => {
  if (scope.kind === 'ambiguous') {
    return (
      ` More than one company is called **${scope.name}**, so this covers every company \u2014 pick ` +
      'the one you mean in the Buyer or Supplier filter on the Invoices page.'
    );
  }
  if (scope.kind === 'unavailable') {
    return ' I could not load the company list, so this covers every company rather than any one you named.';
  }
  return '';
};

/**
 * The Invoices page filtered to the queue. Its dates filter on the day an invoice
 * was created and open on a recent window by default, so an all-time queue's
 * link starts on the oldest invoice's day, or the list would hide it.
 */
const queuePageLink = (
  queue: InvoiceQueue,
  result: StatusQueue,
  dates: StatusQueueFilter,
  scope: CompanyScope,
): KnowledgeLink | undefined => {
  if (!('status' in queue.selector)) return undefined;
  const params = new URLSearchParams({ supplierTransactionStatus: queue.selector.status, showAllFilters: 'true' });
  const oldest = result.firstCreatedOn ?? result.rows[result.rows.length - 1]?.createdOn;
  const startDate = dates.startDate ?? shiftedDay(oldest, -1);
  if (startDate) params.set('startDate', startDate);
  if (dates.endDate) params.set('endDate', dates.endDate);
  for (const [key, value] of Object.entries(accountFilter(scope))) {
    if (value) params.set(key, value);
  }
  return { label: 'Open these invoices', path: `/admin-invoices?${params.toString()}` };
};

const holdFlagUnsupported = (queue: InvoiceQueue, rows: InvoiceLookupRow[]): boolean =>
  !('status' in queue.selector) && rows.some((row) => row.supplierPayoutOnHold !== true);

const answerAdminQueue = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { message, period } = context;
  const queue = pickAdminQueue(message);
  if (!queue) {
    return {
      text:
        'Which invoices do you mean? I can list failed payments, payouts on hold, failed payouts, ' +
        'charges on hold, refund requests, denied refunds, uncollectible or declined invoices, ' +
        'pending payments or payouts, invoices ready for payout, and manual payments.',
    };
  }

  const dates: StatusQueueFilter = period.explicit ? apiDateRange(period) : {};
  const scope = await resolveCompany(message, queue.side);
  const result = await findInvoiceQueue('admin', queue.selector, { ...dates, ...accountFilter(scope) });

  if (holdFlagUnsupported(queue, result.rows)) {
    // The search ignored the hold filter — the server predates it — so the page
    // is every invoice, not the held ones.
    return {
      text:
        'I cannot list held payouts yet. Each invoice shows **Payout on hold** on the admin ' +
        'Invoices page, and a lookup such as _"invoice 1234567"_ tells you whether its payout is held.',
      link: { label: 'Open Invoices', path: '/admin-invoices' },
    };
  }

  const { count, rows } = result;
  const when = period.explicit ? ` created ${period.label}` : '';
  const now = period.explicit ? '' : ' right now';
  const scopeText = scopePhrase(scope);
  const note = scopeNote(scope);
  const link = queuePageLink(queue, result, dates, scope);

  if (count === 0) {
    return {
      text: `No invoices${scopeText}${when} ${queue.has[1]}${now}.${note}`,
      link,
    };
  }

  // Refunds move money the other way, so they are listed but kept out of the total.
  const charges = rows.filter((row) => !isRefund(row));
  const refunds = rows.length - charges.length;
  const total = money(totalOf(charges, queue.side));
  const amountWord = queue.side === 'supplier' ? 'in supplier payouts' : 'charged to buyers';
  const refundNote = refunds
    ? ` ${pluralize(refunds, 'of them is a refund', 'of them are refunds')}, left out of that total.`
    : '';
  const totalSentence =
    count > rows.length
      ? ` The ${rows.length} most recent come to **${total}** ${amountWord}.${refundNote}`
      : ` They come to **${total}** ${amountWord}.${refundNote}`;
  const lead =
    count === 1
      ? `**1 invoice**${scopeText}${when} ${queue.has[0]}${now}.`
      : `**${count} invoices**${scopeText}${when} ${queue.has[1]}${now}.`;
  const listed = count > MAX_LISTED ? ` The ${MAX_LISTED} most recent are listed below.` : '';

  return {
    text: `${lead}${count === 1 ? '' : totalSentence}${queue.meaning ? ` ${queue.meaning}` : ''}${listed}${note}`,
    breakdown: rows.slice(0, MAX_LISTED).map((row) => adminRow(row, queue)),
    link,
  };
};

const FAILED: QueueSelector = { status: 'PAYMENT_FAILED' };
const PENDING: QueueSelector = { status: 'PAYMENT_PENDING' };
const AWAITING_APPROVAL: QueueSelector = { status: 'NEEDS_CUSTOMER_APPROVAL' };

const PAYMENT_FAILED_BOOKINGS: KnowledgeLink = {
  label: 'Open Payment Failed bookings',
  path: '/bookings?status=PAYMENT_FAILED',
};

const BUYER_INVOICES: KnowledgeLink = { label: 'Open Invoices', path: '/reports/buyer-invoices' };

const buyerRow = (row: InvoiceLookupRow): KnowledgeBreakdownRow => ({
  label: invoiceLabel(row),
  value: [row.locationName?.trim(), money(amountOf(row, 'buyer')), displayDate(row.createdOn), processorMessage(row)]
    .filter(Boolean)
    .join(' \u00b7 '),
  link: invoiceLink('buyer', row),
});

const bookingList = (rows: InvoiceLookupRow[]): string => {
  const numbers = Array.from(
    new Set(rows.map((row) => (row.bookingNumber ?? '').trim()).filter((value) => value !== '')),
  );
  if (numbers.length === 0) return '';
  const shown = numbers.slice(0, 5).map((number) => `**#${number}**`);
  const more = numbers.length > shown.length ? ` and ${numbers.length - shown.length} more` : '';
  return ` on ${numbers.length === 1 ? 'booking' : 'bookings'} ${shown.join(', ')}${more}`;
};

const countFailedRefunds = async (dates: StatusQueueFilter): Promise<number> => {
  const counts = await Promise.all(
    REFUND_TRANSACTION_TYPES.map((transactionType) =>
      countInvoiceQueue('buyer', FAILED, { ...dates, transactionType }),
    ),
  );
  return counts.reduce((sum, value) => sum + value, 0);
};

const answerBuyerFailedPayments = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { period } = context;
  const dates: StatusQueueFilter = period.explicit ? apiDateRange(period) : {};
  const [failed, pending, awaitingApproval] = await Promise.all([
    findInvoiceQueue('buyer', FAILED, dates),
    countInvoiceQueue('buyer', PENDING, dates),
    countInvoiceQueue('buyer', AWAITING_APPROVAL, dates),
  ]);

  // A refund whose payment failed is money owed to the buyer, not by them. Past one
  // page the refunds on unseen pages count too, so they are counted on the server, one
  // type at a time because the search takes a single transactionType.
  const owed = failed.rows.filter((row) => !isRefund(row));
  const count =
    failed.count > failed.rows.length
      ? Math.max(owed.length, failed.count - (await countFailedRefunds(dates)))
      : owed.length;
  const when = period.explicit ? ` from invoices created ${period.label}` : '';

  const extras = [
    pending > 0
      ? `${pluralize(pending, 'payment is', 'payments are')} still processing \u2014 nothing to do for those.`
      : '',
    awaitingApproval > 0
      ? `${pluralize(awaitingApproval, 'invoice is', 'invoices are')} waiting for your approval before ` +
        `${awaitingApproval === 1 ? 'it is' : 'they are'} charged.`
      : '',
  ].filter(Boolean);
  const extraText = extras.length ? ` ${extras.join(' ')}` : '';

  if (count <= 0) {
    return {
      text: `You have no failed payments${when} \u2014 nothing is waiting on you to pay.${extraText}`,
      link: BUYER_INVOICES,
    };
  }

  const total = money(totalOf(owed, 'buyer'));
  const lead =
    count === 1
      ? `**1 invoice** has a failed payment${when}, for **${total}**${bookingList(owed)}.`
      : count > owed.length
        ? `**${count} invoices** have a failed payment${when}; the ${owed.length} most recent come to ` +
          `**${total}**${bookingList(owed)}.`
        : `**${count} invoices** have a failed payment${when}, totalling **${total}**${bookingList(owed)}.`;

  return {
    text:
      `${lead} To pay, open **Bookings \u2192 Payment Failed**, choose the booking and select ` +
      '**Make Payment** \u2014 update the payment method first if the card or bank account has ' +
      `changed. A booking cannot be used until its payment goes through.${extraText}`,
    breakdown: owed.slice(0, MAX_LISTED).map(buyerRow),
    link: PAYMENT_FAILED_BOOKINGS,
  };
};

export const adminInvoiceQueueQuery: KnowledgeQuery = {
  id: 'adminInvoiceQueue',
  audiences: ['admin'],
  // One queue or one ranking now cannot say how it changed month to month.
  declines: asksForTimeComparison,
  synonyms: [
    'which invoices have a failed payment',
    'invoices with failed payments',
    'which payouts are on hold',
    'invoices with payouts on hold',
    'which payouts failed',
    'invoices with failed payouts',
    'open refund requests',
    'uncollectible invoices',
    'invoices ready for payout',
  ],
  claims: claimsAdminInvoiceQueue,
  needsLocations: false,
  example: 'which invoices have a failed payment',
  description:
    'Lists the invoices in one status queue across the platform, for an administrator: failed ' +
    'payments (still unpaid), payouts on hold, failed payouts, charges on hold, refund requests, ' +
    'denied refunds, uncollectible, declined charges, pending payments or payouts, ready for ' +
    'payout, or manual payments. Gives how many there are, what they come to, the most recent ' +
    'with buyer, supplier and booking, and a link to the Invoices page filtered to that status. ' +
    'Can be narrowed to one company named in the question and to invoices created in a period. ' +
    'Not a total invoice count and not one invoice\u2019s status (use the invoice lookup for that).',
  answer: answerAdminQueue,
};

export const buyerFailedPaymentsQuery: KnowledgeQuery = {
  id: 'buyerFailedPayments',
  audiences: ['buyer'],
  // One queue or one ranking now cannot say how it changed month to month.
  declines: asksForTimeComparison,
  synonyms: [
    'do i have any failed payments',
    'do i owe anything',
    'what do i owe',
    'how much do i owe',
    'do i have unpaid invoices',
    'any unpaid invoices',
    'any overdue invoices',
  ],
  claims: claimsBuyerFailedPayments,
  needsLocations: false,
  example: 'do I have any failed payments',
  description:
    'For a buyer: whether any of their invoices has a failed payment and so is still owed \u2014 ' +
    'how many, what they come to, on which bookings \u2014 and how to pay them from Bookings \u2192 ' +
    'Payment Failed. Also says how many payments are still processing and how many invoices ' +
    'await the buyer\u2019s approval. Answers "do I owe anything". Not overage totals and not ' +
    'why one booking\u2019s payment failed.',
  answer: answerBuyerFailedPayments,
};

export const __testing = { ADMIN_QUEUES, MAX_LISTED, resolveCompany };
